/**
 * API fetch 래퍼
 *
 * docs/api_spec.md 의 응답 envelope ({ ok, data } / { ok: false, error }) 자동 처리.
 *
 * 사용:
 *   const sales = await api.get<Sales[]>('/sales?page=1');
 *   await api.post('/sales', { ... });
 *
 * 에러는 ApiCallError 로 throw. 호출 측에서 try/catch 또는 TanStack Query 의
 * onError 로 처리.
 */

import type { ApiResponse } from './types';

const BASE = '/api';

export class ApiCallError extends Error {
  constructor(
    public code: string,
    message: string,
    public details?: unknown,
    public status?: number,
  ) {
    super(message);
    this.name = 'ApiCallError';
  }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  let json: ApiResponse<T>;
  try {
    json = (await res.json()) as ApiResponse<T>;
  } catch {
    throw new ApiCallError('INTERNAL_ERROR', `응답 파싱 실패 (HTTP ${res.status})`, undefined, res.status);
  }

  if (!json.ok) {
    // 우리 모양이 아닌 오류(앞단 중계 서버의 404·502 등)는 error 가 객체가 아니다 — 빈 문구를 띄우지 않게 (v2.232.0 · F-15)
    const e = json.error as unknown;
    if (!e || typeof e !== 'object') {
      const raw = json as unknown as { message?: unknown };
      const msg = typeof raw.message === 'string' && raw.message ? raw.message : `요청을 처리하지 못했습니다 (HTTP ${res.status})`;
      throw new ApiCallError(res.status === 404 ? 'NOT_FOUND' : 'INTERNAL_ERROR', msg, undefined, res.status);
    }
    throw new ApiCallError(json.error.code, json.error.message, json.error.details, res.status);
  }
  return json.data;
}

export const api = {
  get: <T>(path: string) => call<T>('GET', path),
  post: <T>(path: string, body?: unknown) => call<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => call<T>('PATCH', path, body),
  put: <T>(path: string, body?: unknown) => call<T>('PUT', path, body),
  delete: <T>(path: string) => call<T>('DELETE', path),
};
